<template>
  <div class="q-pa-md">
    <q-table
      title="Sessions"
      :rows="sessions"
      :columns="columns"
      row-key="applicationId"
      :visible-columns="visibleColumns"
      v-model:pagination="pagination"
      :rows-per-page-options="[5, 10, 25, 50]"
      @request="onRequest"
    >
      <template v-slot:top-right>
        <q-select
          class="q-mr-xs"
          v-model="visibleColumns"
          multiple
          outlined
          dense
          options-dense
          display-value="Filter"
          emit-value
          map-options
          :options="columns"
          option-value="name"
          options-cover
          style="min-width: 150px"
        >
          <template #option="scope">
            <q-item
              clickable
              @click="scope.toggleOption(scope.opt)"
            >
              <q-item-section side>
                <q-checkbox
                  :model-value="scope.selected"
                  @click="scope.toggleOption(scope.opt)"
                />
              </q-item-section>

              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          v-if="showRetired"
          class="q-mr-xs"
          color="grey"
          icon="history_toggle_off"
          no-caps
          @click="fetchSessions(true); showRetired = !showRetired"
        />
        <q-btn
          v-else
          class="q-mr-xs"
          color="primary"
          icon="restore"
          no-caps
          @click="fetchSessions(false); showRetired = !showRetired"
        />

        <q-btn
          v-if="hasRoles('HRL', 'R&D')"
          :to="{ name: 'SessionStepper' }"
          class="q-mr-xs"
          color="primary"
          icon-right="add"
          no-caps
        />
      </template>
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
          >
            {{ col.label }}
          </q-th>
          <q-th>
            Description
          </q-th>
          <q-th v-if="hasRoles('R&D', 'HRL')">
            Edit
          </q-th>
          <q-th v-else>
            View
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr
          :props="props"
          :style="props.row.isOpen ==='Open' ? '' : 'background-color: #f1f1f1;'"
        >
          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
          >
            <div>
              {{ col.value }}
            </div>
          </q-td>
          <q-td>
            <q-btn
              @click="showSessionDescription = true; setSessionId(props.row)"
              class="q-mr-xs"
              size="sm"
              text-color="primary"
              color="white"
              round
              dense
              flat
              icon="description"
            />
          </q-td>
          <q-td>
            <q-btn
              @click="showSessionDialogue = true; setSessionId(props.row)"
              class="q-mr-xs"
              size="sm"
              text-color="primary"
              color="white"
              flat
              round
              dense
              :icon="hasRoles('R&D', 'HRL') ? 'edit' : 'visibility'"
            />
          </q-td>
        </q-tr>
        <q-tr
          v-show="props.expand"
          :props="props"
        >
          <q-td colspan="100%">
            <div
              class="text-left"
              v-for="col in props.cols"
              :key="col.name"
              :props="props"
            >
              <span class="text-body1">
                {{ col.name }}:
                {{ col.value }}
              </span>
            </div>
          </q-td>
        </q-tr>
      </template>
    </q-table>
    <q-dialog
      ref="editModal"
      v-model="showSessionDialogue"
    >
      <q-card style="width: 750px; max-width: 80vw;">
        <SubmitSession ref="submitSessionForm" />
        <!-- <div v-if="hasRoles('HRL', 'R&D')"> -->
        <ComplexesTreeSelector />
        <!-- </div> -->
        <q-card-actions
          align="center"
          class="bg-white text-teal"
        >
          <q-btn
            flat
            :label="isRa ? 'Close' : 'Cancel'"
            v-close-popup
            color="primary"
          />
          <q-btn
            v-if="!isRa"
            @click="callUpdateSession()"
            flat
            label="Save"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-model="showSessionDescription">
      <q-card style="width: 750px; max-width: 80vw;">
        <SessionDescription />
        <q-card-actions
          align="center"
          class="bg-white text-teal"
        >
          <q-btn
            flat
            label="Close"
            v-close-popup
            color="primary"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ref } from 'vue';
import { date, useQuasar } from 'quasar';
import useAuth from '@/hooks/auth';
import useSession from '@/hooks/session';
import useUiUtilities from '@/utilities/uiUtilities';
import SubmitSession from './SubmitSession.vue';
import ComplexesTreeSelector from './ComplexesTreeSelector.vue';
import SessionDescription from './SessionDescription.vue';

export default {
  async setup() {
    const $q = useQuasar();
    const { hasRoles } = useAuth();
    const {
      sessionId,
      updateSession,
      isSessionFormValid,
      sessionDetails,
      fetchSession,
    } = useSession();

    const { notifyResult } = useUiUtilities();

    const columns = [
      {
        name: 'Session Name', align: 'left', label: 'Session Name', field: 'sessionName', sortable: true,
      },
      {
        name: 'Session Type', align: 'left', label: 'Session Type', field: 'sessionType', sortable: true,
      },
      {
        name: 'Start Date', align: 'left', label: 'Start Date', field: 'startDate', sortable: true, format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'End Date', align: 'left', label: 'End Date', field: 'endDate', sortable: true, format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Move In Date', align: 'left', label: 'Move In Date', field: 'moveInDate', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Move Out Date', align: 'left', label: 'Move Out Date', field: 'moveOutDate', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Application Deadline', align: 'left', label: 'Application Deadline', field: 'applicationDeadline', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Placement Communication Date', align: 'left', label: 'Placement Communication Date', field: 'confirmationDeadline', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Student Facing Application Deadline', align: 'left', label: 'Student Facing Application Deadline', field: 'applicationLockDate', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Open', align: 'left', label: 'Open', field: 'isOpen',
      },
    ];

    const showRetired = ref(false);
    const submitSessionForm = ref();
    const hasUpdateCompleted = ref(false);
    const editModal = ref(null);
    const isRa = !hasRoles('HRL', 'R&D') && hasRoles('RESIDENT-ASSISTANT');

    const sessions = ref();
    const pagination = ref({
      rowsPerPage: 10,
      rowsNumber: -1,
      page: 1,
      sortBy: 'desc',
      descending: false,
    });

    const setSessionId = (row) => {
      sessionId.value = row.sessionId;
    };

    const fetchSessions = async (onlyOpen, page, rowsPerPage) => {
      try {
        const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Sessions`;
        const response = await axios.get(url, {
          params: {
            page,
            rowsPerPage,
            onlyOpen,
          },
        });
        const { data } = response;
        sessions.value = data.data;
        pagination.value.rowsNumber = data.rowsNumber;
        pagination.value.page = data.page;
        pagination.value.rowsPerPage = data.rowsPerPage;
      } catch (error) {
        console.log(error);
        console.log(error.response.status, error.response);
        notifyResult(error.response.status, error.response);
      }
    };

    await fetchSessions(true);

    const onRequest = async (props) => {
      await fetchSessions(
        !showRetired.value,
        props.pagination.page,
        props.pagination.rowsPerPage,
      );
    };

    const updateRow = async () => {
      for (let i = 0; i < sessions.value.length; i += 1) {
        if (sessions.value[i].sessionId === sessionId.value) {
          sessions.value[i] = sessionDetails.value;

          return;
        }
      }
    };

    const callUpdateSession = async () => {
      $q.loading.show();
      await submitSessionForm.value.onValidate();
      if (isSessionFormValid.value) {
        const updateResponse = await updateSession(sessionId.value);
        notifyResult(updateResponse.status, updateResponse);
        if (updateResponse.status === 200 || updateResponse.status === 201) {
          await fetchSession(sessionId.value);
          await updateRow();
          editModal.value.hide();
        }
      }

      $q.loading.hide();
    };

    return {
      editModal,
      showRetired,
      fetchSessions,
      isRa,
      setSessionId,
      showSessionDialogue: ref(false),
      showSessionDescription: ref(false),
      onRequest,
      visibleColumns: ref([
        'Session Name',
        'Session Type',
        'Start Date',
        'End Date',
      ]),
      columns,
      pagination,
      sessions,
      callUpdateSession,
      hasRoles,
      isSessionFormValid,
      submitSessionForm,
      hasUpdateCompleted,
    };
  },
  components: {
    SubmitSession,
    ComplexesTreeSelector,
    SessionDescription,
  },
};
</script>

<style>
</style>
